import { CommonModule } from "@angular/common";
import { Component, computed, effect, inject, input, OnInit, output, signal } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { LucideAngularModule, Star, X } from "lucide-angular";
import { filter } from "rxjs";
import cn from "../../../../util/cn";
import { NavDto } from "../shell-default-uif/NavDto";

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: "li[app-shell-navitem-uif]",
  imports: [LucideAngularModule, CommonModule],
  templateUrl: "./shell-navitem-uif.component.html",
  styles: ``,
})
export class ShellNavitemUifComponent implements OnInit {
  //#region I/O
  navDto = input.required<NavDto>();
  // Parent-controlled active state (if provided)
  manualActive = input<boolean | undefined>(undefined);
  globalItem = input(false);
  tabable = input(false);

  Star = Star;
  X = X;

  canBeFavorized = input(false);
  isFavorized = input(false);
  toggledFavourite = output<string>();
  //#endregion

  //#region Fields
  activeBackgroundShade = signal(
    " bg-primary hover:bg-primary focus:text-white focus:text-white focus:bg-primary text-white rounded-none",
  );
  currentUrl = signal("/");
  router = inject(Router);
  active = signal(false);
  classes = computed(() =>
    cn(this.active() ? this.activeBackgroundShade() : "rounded-none w-full active:text-gray-900"),
  );
  hover = signal(false);

  dropdownVisible = signal(false);
  //#endregion

  // Field initializer: if manualActive is provided, update active accordingly.
  private manualActiveEffect = effect(() => {
    if (this.manualActive() !== undefined) {
      this.active.set(this.manualActive()!);
    }
  });

  //#region UI Events

  constructor() {
    effect(() => {
      if (this.manualActive() !== undefined) {
        this.dropdownVisible.set(this.manualActive()!);
      }
    });

    effect(() => {
      if (this.navDto().isOpen()) {
        this.dropdownVisible.set(true);
      }
    });
  }

  onDropdownLabelClicked() {
    this.dropdownVisible.update((prev) => !prev);
  }

  onClicked() {
    const route = this.navDto().route;
    if (route) {
      this.router.navigate([route()]);
    }
    if (this.navDto().children.length > 0) {
      this.dropdownVisible.update((prev) => !prev);
    }
  }

  toggleFavourite() {
    const route = this.navDto().route;
    if (route) {
      this.toggledFavourite.emit(route());
    }
  }

  //#endregion

  //#region OnInit
  ngOnInit() {
    // Only subscribe to router events if the parent isn't controlling the active state.
    if (this.manualActive() === undefined) {
      const route = this.navDto().route;
      if (!route) {
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
          this.active.set(false);
        });
      } else {
        // Set the initial URL (if needed)
        this.currentUrl.set(this.router.url);
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
          const currentRoute = this.navDto().route;
          if (!currentRoute) {
            this.active.set(false);
            return;
          }
          let isActive = this.router.isActive(currentRoute(), false);
          const altRoutes = this.navDto().alternateRoutes;
          if (!isActive && altRoutes) {
            const currentUrl = this.router.url.replace(/^\//, '');
            const routes = altRoutes();
            isActive = routes.some((r) => currentUrl.startsWith(r));
          }
          this.active.set(isActive);
        });
      }
    }
  }

  //#endregion
}
